import SimpleDropdown from '@/components/simple-dropdown';
import TextStack, {
  TextLabel,
  TextStackItem,
  TextValue
} from '@/components/text-stack';
import STATE_CODES from '@/consts/states.constants';
import { useSnackbar } from '@/contexts/SnackBarContext';
import {
  DeconversionDestinationDto,
  DeconversionDestinationStatus,
  DestinationFormData,
  UpdateDestinationStatus
} from '@/models/PlanDeconversionDTO.model';
import { PlanService } from '@/services/Plan.service';
import { userService } from '@/services/User.service';
import formatters from '@/utils/Formatters';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { LoadingButton } from '@mui/lab';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  DrawerProps,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Form, Formik } from 'formik';
import { FC, useMemo } from 'react';

interface DeconversionDestinationDrawerProps extends DrawerProps {
  data?: DeconversionDestinationDto;
  onClose: () => void;
}

const StyledCard = styled(Card)(props => ({
  backgroundColor: alpha(props.theme.palette.primary.main, 0.04),
  border: `1px solid ${props.theme.palette.primary.main}`,
  marginBottom: props.theme.spacing(2)
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: theme.zIndex.vestwellAppBar + 1
}));

const StyledText = styled(Typography)(() => ({
  textDecoration: 'line-through'
}));

export const DeconversionDestinationDrawer: FC<
  DeconversionDestinationDrawerProps
> = (props: DeconversionDestinationDrawerProps) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const userInfo = userService.getUser();

  const initDestination: DestinationFormData = {
    bankAccountName: props.data?.bankInfo.accountName,
    bankAccountNumber: props.data?.bankInfo.accountNumber,
    bankName: props.data?.bankInfo.name,
    bankRoutingNumber: props.data?.bankInfo.routingNumber,
    name: props.data?.name,
    payeeAddress: props.data?.payeeInfo.address,
    payeeCity: props.data?.payeeInfo.city,
    payeeCountry: props.data?.payeeInfo.country,
    payeeName: props.data?.payeeInfo.name,
    payeeState: props.data?.payeeInfo.state,
    payeeZip: props.data?.payeeInfo.zip
  };
  const muiTheme = useTheme();
  const expandedDrawerTransition = muiTheme.transitions.create('width');

  const canApprove = useMemo(() => {
    return (
      !props.data?.updatedBy || userInfo.nickname !== props.data?.updatedBy.id
    );
  }, [props.data?.updatedBy, userInfo]);

  const editDisabled = useMemo(() => {
    return props.data?.status === DeconversionDestinationStatus.PendingApproval;
  }, [props.data?.status]);

  const createDestinationMutation = useMutation(
    ['PlanService.postDeconversionDestination'],
    (destination: DestinationFormData) => {
      return PlanService.postDeconversionDestination(destination);
    },
    {
      onSuccess: () => {
        showSnackbar({
          message: 'Success!',
          severity: 'success'
        });
        queryClient.refetchQueries(['PlanService.deconversionDestinations']);
        props.onClose();
      }
    }
  );

  const updateDestinationMutation = useMutation(
    ['PlanService.patchDeconversionDestination'],
    (args: {
      destination?: DestinationFormData;
      status: UpdateDestinationStatus;
    }) => {
      return PlanService.patchDeconversionDestination({
        ...(args.destination
          ? Object.keys(args.destination).reduce<DestinationFormData>(
              (acc, key) => {
                if (args.destination[key] !== initDestination[key]) {
                  acc[key] = args.destination[key];
                }

                return acc;
              },
              {} as DestinationFormData
            )
          : {}),
        id: props.data?.id,
        status: args.status
      });
    },
    {
      onSuccess: () => {
        showSnackbar({
          message: 'Success!',
          severity: 'success'
        });
        queryClient.refetchQueries(['PlanService.deconversionDestinations']);
        props.onClose();
      }
    }
  );

  return (
    <StyledDrawer
      SlideProps={{
        onEntered: node =>
          (node.style.transition = node.style.transition
            ? `${node.style.transition},${expandedDrawerTransition}`
            : expandedDrawerTransition)
      }}
      anchor='right'
      onClose={props.onClose}
      open={props.open}>
      <Box padding={2}>
        <Stack direction='row' justifyContent='space-between'>
          <Typography variant='h6'>DestinationDetails</Typography>
          <IconButton aria-label='close' onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {props.data?.status ===
          DeconversionDestinationStatus.PendingApproval && (
          <StyledCard variant='outlined'>
            <CardContent>
              <Stack direction='row' justifyContent='space-between' mb={2}>
                <Typography variant='h6'>Review Changes</Typography>
                {canApprove ? (
                  <Stack direction='row' spacing={2}>
                    <Button
                      color='error'
                      onClick={() => {
                        updateDestinationMutation.mutate({
                          status: UpdateDestinationStatus.Reject
                        });
                      }}
                      startIcon={<RemoveCircleOutlineIcon />}
                      variant='contained'>
                      Reject
                    </Button>
                    <Button
                      onClick={() => {
                        updateDestinationMutation.mutate({
                          status: UpdateDestinationStatus.Approve
                        });
                      }}
                      startIcon={<CheckCircleOutlineIcon />}
                      variant='contained'>
                      Approve
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    color='error'
                    onClick={() => {
                      updateDestinationMutation.mutate({
                        status: UpdateDestinationStatus.Cancel
                      });
                    }}
                    startIcon={<RemoveCircleOutlineIcon />}
                    variant='contained'>
                    Cancel
                  </Button>
                )}
              </Stack>
              <Divider />
              {props.data?.pendingChanges && (
                <TextStack direction='column'>
                  {props.data?.pendingChanges.name && (
                    <TextStackItem>
                      <TextLabel>Destination</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.name}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.name}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.payeeInfo?.name && (
                    <TextStackItem>
                      <TextLabel>Payee Name</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.payeeInfo.name}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.payeeInfo.name}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.payeeInfo?.address && (
                    <TextStackItem>
                      <TextLabel>Payee Address</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.payeeInfo.address}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.payeeInfo.address}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.payeeInfo?.city && (
                    <TextStackItem>
                      <TextLabel>Payee City</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.payeeInfo.city}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.payeeInfo.city}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.payeeInfo?.state && (
                    <TextStackItem>
                      <TextLabel>Payee State</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.payeeInfo.state}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.payeeInfo.state}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.payeeInfo?.zip && (
                    <TextStackItem>
                      <TextLabel>Payee Zip</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.payeeInfo.zip}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.payeeInfo.zip}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.payeeInfo?.country && (
                    <TextStackItem>
                      <TextLabel>Payee Country</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.payeeInfo.country}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.payeeInfo.country}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.bankInfo?.name && (
                    <TextStackItem>
                      <TextLabel>Bank Name</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.bankInfo.name}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.bankInfo.name}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.bankInfo?.accountName && (
                    <TextStackItem>
                      <TextLabel>Bank Account Name</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.bankInfo.accountName}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.bankInfo.accountName}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.bankInfo?.routingNumber && (
                    <TextStackItem>
                      <TextLabel>Bank Routing Number</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.bankInfo.routingNumber}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.bankInfo.routingNumber}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                  {props.data?.pendingChanges.bankInfo?.accountNumber && (
                    <TextStackItem>
                      <TextLabel>Bank Account Number</TextLabel>
                      <TextValue>
                        <Stack
                          direction='row'
                          justifyItems='center'
                          spacing={1}>
                          <StyledText variant='body1'>
                            {props.data?.bankInfo.accountNumber}
                          </StyledText>
                          <ArrowForwardIcon />
                          <Typography variant='body1'>
                            {props.data?.pendingChanges.bankInfo.accountNumber}
                          </Typography>
                        </Stack>
                      </TextValue>
                    </TextStackItem>
                  )}
                </TextStack>
              )}
              <Typography mt={2} variant='body2'>
                {props.data?.pendingChanges
                  ? `${formatters.formatFromIsoDateCustom(props.data.updatedAt, 'M/DD/YY HH:mm')} Changes submitted by ${props.data.updatedBy.id}`
                  : `${formatters.formatFromIsoDateCustom(props.data?.createdAt, 'M/DD/YY HH:mm')} Created by ${props.data?.createdBy.id}`}
              </Typography>
            </CardContent>
          </StyledCard>
        )}
        <Formik
          initialValues={initDestination}
          onSubmit={values => {
            if (props.data) {
              updateDestinationMutation.mutate({
                destination: values,
                status: UpdateDestinationStatus.Update
              });
            } else {
              createDestinationMutation.mutate(values);
            }
          }}>
          {formik => (
            <Form>
              <Stack spacing={3}>
                <TextField
                  disabled={editDisabled}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  fullWidth
                  label='Destination'
                  name='name'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  size='small'
                  value={formik.values.name}
                />
                <Divider textAlign='left'>Payee Info</Divider>
                <Stack spacing={2}>
                  <TextField
                    disabled={editDisabled}
                    error={
                      formik.touched.name && Boolean(formik.errors.payeeName)
                    }
                    fullWidth
                    label='Payee Name'
                    name='payeeName'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    size='small'
                    value={formik.values.payeeName}
                  />
                  <TextField
                    disabled={editDisabled}
                    error={
                      formik.touched.name && Boolean(formik.errors.payeeAddress)
                    }
                    fullWidth
                    label='Payee Address'
                    name='payeeAddress'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    size='small'
                    value={formik.values.payeeAddress}
                  />
                  <Grid2 container spacing={2}>
                    <Grid2 xs={6}>
                      <TextField
                        disabled={editDisabled}
                        error={
                          formik.touched.name &&
                          Boolean(formik.errors.payeeCity)
                        }
                        fullWidth
                        label='City'
                        name='payeeCity'
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        size='small'
                        value={formik.values.payeeCity}
                      />
                    </Grid2>
                    <Grid2 xs={3}>
                      <SimpleDropdown
                        disabled={editDisabled}
                        fieldId='payeeState'
                        fieldName='State'
                        fieldValues={STATE_CODES}
                        size='small'
                      />
                    </Grid2>
                    <Grid2 xs={3}>
                      <TextField
                        disabled={editDisabled}
                        error={
                          formik.touched.name && Boolean(formik.errors.payeeZip)
                        }
                        fullWidth
                        label='Zip'
                        name='payeeZip'
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        size='small'
                        value={formik.values.payeeZip}
                      />
                    </Grid2>
                  </Grid2>
                  <SimpleDropdown
                    disabled={editDisabled}
                    fieldId='payeeCountry'
                    fieldName='Country'
                    fieldValues={['USA', 'Mexico', 'Canada', 'Other']}
                    size='small'
                  />
                </Stack>
                <Divider textAlign='left'>Receiving Bank</Divider>
                <Stack spacing={2}>
                  <TextField
                    disabled={editDisabled}
                    error={
                      formik.touched.name && Boolean(formik.errors.bankName)
                    }
                    fullWidth
                    label='Bank Name'
                    name='bankName'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    size='small'
                    value={formik.values.bankName}
                  />
                  <TextField
                    disabled={editDisabled}
                    error={
                      formik.touched.name &&
                      Boolean(formik.errors.bankAccountName)
                    }
                    fullWidth
                    label='Account Name'
                    name='bankAccountName'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    size='small'
                    value={formik.values.bankAccountName}
                  />
                  <TextField
                    disabled={editDisabled}
                    error={
                      formik.touched.name &&
                      Boolean(formik.errors.bankRoutingNumber)
                    }
                    fullWidth
                    label='Routing Number'
                    name='bankRoutingNumber'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    size='small'
                    value={formik.values.bankRoutingNumber}
                  />
                  <TextField
                    disabled={editDisabled}
                    error={
                      formik.touched.name &&
                      Boolean(formik.errors.bankAccountNumber)
                    }
                    fullWidth
                    label='Account Number'
                    name='bankAccountNumber'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    size='small'
                    value={formik.values.bankAccountNumber}
                  />
                </Stack>
                <Stack direction='row' spacing={2}>
                  <LoadingButton
                    disabled={!formik.dirty}
                    loading={createDestinationMutation.isLoading}
                    type='submit'
                    variant='contained'>
                    Submit for approval
                  </LoadingButton>
                  <Button disabled={!formik.dirty} type='reset' variant='text'>
                    Reset changes
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </StyledDrawer>
  );
};
